"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherToggleLeft,
  FeatherToggleRight,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

/**
 * A nursery's register of units checked one by one (extinguishers, lights, call
 * points). Presentational: each unit type owns its generated hooks and passes results down.
 */
export interface RegisteredUnit {
  id: string;
  name: string;
  /** Null only for unit types that may be registered without a room. */
  room: { id: string; name: string } | null;
  notes: string | null;
  isActive: boolean;
}

export interface RegisteredUnitCopy {
  /** Section heading, e.g. "Emergency Lights". */
  title: string;
  /** One line under the heading explaining what the register is for. */
  description: string;
  /** Primary action, e.g. "Add Light". */
  addLabel: string;
  /** Empty-state heading, e.g. "No emergency lights yet". */
  emptyTitle: string;
  /** Empty-state body — say what registering them enables. */
  emptyBody: string;
  /** Empty-state icon; each unit type brings its own. */
  emptyIcon: ReactNode;
  /** Plural noun for the pagination summary, e.g. "lights". */
  itemNoun: string;
  /** Shown when the list fails to load. */
  errorMessage: string;
}

interface RegisteredUnitTableProps {
  units: RegisteredUnit[];
  isLoading: boolean;
  error: unknown;
  copy: RegisteredUnitCopy;
  onAdd: () => void;
  onEdit: (unit: RegisteredUnit) => void;
  onToggleActive: (unit: RegisteredUnit) => void;
}

export function RegisteredUnitTable({
  units,
  isLoading,
  error,
  copy,
  onAdd,
  onEdit,
  onToggleActive,
}: RegisteredUnitTableProps) {
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full set, so the page is sliced client-side.
  const pagedUnits = useMemo(
    () => units.slice((page - 1) * pageSize, page * pageSize),
    [units, page, pageSize],
  );

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {copy.title}
          </span>
          <span className="text-body font-body text-subtext-color">
            {copy.description}
          </span>
        </div>
        <Button variant="brand-primary" icon={<FeatherPlus />} onClick={onAdd}>
          {copy.addLabel}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : error ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
          <IconWithLogoBackground variant="error" size="x-large" />
          <span className="text-body font-body text-subtext-color">
            {copy.errorMessage}
          </span>
        </div>
      ) : units.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
          <IconWithLogoBackground
            variant="neutral"
            size="x-large"
            icon={copy.emptyIcon}
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {copy.emptyTitle}
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              {copy.emptyBody}
            </span>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={onAdd}
          >
            {copy.addLabel}
          </Button>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Name / number</Table.HeaderCell>
                <Table.HeaderCell>Room</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="items-center justify-end">
                  Actions
                </Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {pagedUnits.map((unit) => (
              <Table.Row key={unit.id}>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {unit.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {unit.room?.name ?? "No room"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {unit.isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="neutral">Inactive</Badge>
                  )}
                </Table.Cell>
                <Table.Cell variant="aligned-right">
                  <div className="flex items-center justify-end gap-2">
                    <SubframeCore.DropdownMenu.Root>
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <IconButton
                          variant="neutral-primary"
                          size="medium"
                          icon={<FeatherMoreHorizontal />}
                        />
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="end"
                          sideOffset={4}
                          asChild={true}
                        >
                          <DropdownMenu>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherPencil />}
                              onClick={() => onEdit(unit)}
                            >
                              Edit
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={
                                unit.isActive ? (
                                  <FeatherToggleLeft />
                                ) : (
                                  <FeatherToggleRight />
                                )
                              }
                              onClick={() => onToggleActive(unit)}
                            >
                              {unit.isActive ? "Deactivate" : "Activate"}
                            </DropdownMenu.DropdownItem>
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>

          <Pagination
            className="pt-4"
            page={page}
            pageSize={pageSize}
            totalCount={units.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            itemNoun={copy.itemNoun}
          />
        </div>
      )}
    </div>
  );
}
